import React, { useState, useEffect, useRef } from "react";
import Papa from "papaparse";
import VoiceCommentary from "./VoiceCommentary";

// 🏎️ Car Class
class Car {
  constructor(maxSpeed = 200, accelRate = 5, brakeRate = 8) {
    this.speed = 0;
    this.maxSpeed = maxSpeed;
    this.accelRate = accelRate;
    this.brakeRate = brakeRate;
    this.targetSpeed = 0;
  }
  
  accelerate(amount = 10) {
    this.targetSpeed = Math.min(this.targetSpeed + amount, this.maxSpeed);
  }
  
  brake(amount = 10) {
    this.targetSpeed = Math.max(this.targetSpeed - amount, 0);
  }
  
  update(deltaTime = 0.016) {
    const diff = this.targetSpeed - this.speed;
    const rate = diff > 0 ? this.accelRate : this.brakeRate;
    this.speed += Math.sign(diff) * Math.min(Math.abs(diff), rate * deltaTime * 60);
  }
  
  getSpeed() {
    return this.speed;
  }
}

export default function RacingLineVisualizer() {
  const [points, setPoints] = useState([]);
  const [transform, setTransform] = useState({
    a: 121000,
    e: -81000,
    c: 200,
    f: 600,
    rotation: 0
  });

  const [showLine, setShowLine] = useState(true);
  const [showDots, setShowDots] = useState(true);
  const [showTrack, setShowTrack] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  
  // 🏎️ Car state
  const [carPosition, setCarPosition] = useState(0);
  const [carSpeed, setCarSpeed] = useState(0);
  const carRef = useRef(new Car(200));
  const animationRef = useRef(null);
  const lastTimeRef = useRef(Date.now());

  // ✅ Convert lat/lon → pixel with scaling + offset + rotation
  const gpsToPixel = (lat, lon, minLat, minLon, center) => {
    const x = (lon - minLon) * transform.a;
    const y = (lat - minLat) * transform.e;

    const rad = (transform.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const rotatedX = cos * (x - center.x) - sin * (y - center.y) + center.x;
    const rotatedY = sin * (x - center.x) + cos * (y - center.y) + center.y;

    return { pixelX: rotatedX + transform.c, pixelY: rotatedY + transform.f };
  };

  // ✅ Parse CSV and group telemetry by timestamp
  useEffect(() => {
    Papa.parse("/lap_2.csv", {
      header: true,
      download: true,
      complete: (result) => {
        const rows = result.data.filter(
          (r) =>
            r.telemetry_name &&
            r.telemetry_value &&
            r.timestamp &&
            r.telemetry_name.startsWith("VBOX_")
        );

        const grouped = {};
        for (const row of rows) {
          const t = row.timestamp;
          if (!grouped[t]) grouped[t] = {};
          grouped[t][row.telemetry_name] = parseFloat(row.telemetry_value);
        }

        const gpsPoints = Object.entries(grouped)
          .map(([timestamp, g]) => ({
            timestamp,
            lat: g.VBOX_Lat_Min,
            lon: g.VBOX_Long_Minutes
          }))
          .filter((p) => p.lat !== undefined && p.lon !== undefined)
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

        console.log("✅ Parsed GPS points:", gpsPoints.length);
        setPoints(gpsPoints);
      },
    });
  }, []);

  // 🎮 Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      const car = carRef.current;
      if (e.key === 'ArrowUp') car.accelerate(20);
      if (e.key === 'ArrowDown') car.brake(30);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // 🏁 Animation loop
  useEffect(() => {
    if (points.length === 0) return;

    const animate = () => {
      const now = Date.now();
      const deltaTime = (now - lastTimeRef.current) / 1000;
      lastTimeRef.current = now;

      const car = carRef.current;
      car.update(deltaTime);
      
      setCarPosition(prev => (prev + car.getSpeed() * 0.02) % points.length);
      setCarSpeed(car.getSpeed());

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [points.length]);

  if (points.length === 0) return <div style={{ color: '#fff', padding: 20 }}>Loading or no GPS data...</div>;

  // Base references
  const minLat = Math.min(...points.map((p) => p.lat));
  const minLon = Math.min(...points.map((p) => p.lon));
  const maxLat = Math.max(...points.map((p) => p.lat));
  const maxLon = Math.max(...points.map((p) => p.lon));

  // Compute center for rotation
  const center = {
    x: ((maxLon - minLon) * transform.a) / 2,
    y: ((maxLat - minLat) * transform.e) / 2,
  };

  // Build polyline points
  const linePoints = points
    .map(({ lat, lon }) => {
      const { pixelX, pixelY } = gpsToPixel(lat, lon, minLat, minLon, center);
      return `${pixelX},${pixelY}`;
    })
    .join(" ");

  // Get car position
  const currentPoint = points[Math.floor(carPosition)];
  const carPixel = currentPoint 
    ? gpsToPixel(currentPoint.lat, currentPoint.lon, minLat, minLon, center)
    : { pixelX: 0, pixelY: 0 };

  const handleTransformChange = (key, value) => {
    // return;
    setTransform((prev) => ({ ...prev, [key]: parseFloat(value) }));
  };

  return (
    <div
      style={{
        background: "linear-gradient(180deg, #0a0015 0%, #1a0033 50%, #0d0020 100%)",
        color: "#fff",
        minHeight: "100vh",
        padding: 20,
      }}
    >
      <h2 style={{ 
        color: '#9d4edd',
        textShadow: '0 0 20px #9d4edd, 0 0 40px #7b2cbf',
        fontFamily: 'monospace',
        letterSpacing: 2
      }}>
        👻 HAUNTED RACING LINE 👻
      </h2>
      <p style={{ color: '#c77dff' }}>{points.length} GPS points from the spirit realm</p>

      {/* 🎙️ Voice Commentary Component */}
      <VoiceCommentary 
        speed={carSpeed}
        position={carPosition}
        totalPoints={points.length}
        enabled={voiceEnabled}
      />

      {/* 👻 Spooky Speed HUD */}
      <div style={{
        background: 'rgba(20,0,40,0.9)',
        padding: '15px 25px',
        borderRadius: 10,
        marginBottom: 20,
        display: 'inline-block',
        border: '2px solid #9d4edd',
        boxShadow: '0 0 30px rgba(157, 78, 221, 0.5), inset 0 0 20px rgba(123, 44, 191, 0.3)'
      }}>
        <div style={{ fontSize: 24, fontWeight: 'bold' }}>
          👻 Speed: <span style={{ color: '#c77dff', textShadow: '0 0 10px #9d4edd' }}>{carSpeed.toFixed(1)}</span> km/h
        </div>
        <div style={{ fontSize: 14, color: '#9d4edd', marginTop: 5 }}>
          🎃 Use ↑↓ arrow keys to haunt the track
        </div>
        <button
          onClick={() => setVoiceEnabled(!voiceEnabled)}
          style={{
            marginTop: 10,
            background: voiceEnabled ? '#9d4edd' : '#333',
            color: '#fff',
            border: voiceEnabled ? '2px solid #c77dff' : '2px solid #555',
            borderRadius: 6,
            padding: '8px 16px',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 'bold',
            boxShadow: voiceEnabled ? '0 0 15px rgba(157, 78, 221, 0.6)' : 'none',
            transition: 'all 0.3s'
          }}
        >
          🎙️ {voiceEnabled ? 'AI Commentary ON' : 'AI Commentary OFF'}
        </button>
      </div>

      {/* 🎛 Visibility Toggles */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ marginRight: 10 }}>
          <input
            type="checkbox"
            checked={showTrack}
            onChange={() => setShowTrack(!showTrack)}
          />{" "}
          Show Track Image
        </label>
        <label style={{ marginRight: 10 }}>
          <input
            type="checkbox"
            checked={showLine}
            onChange={() => setShowLine(!showLine)}
          />{" "}
          Show Racing Line
        </label>
        <label>
          <input
            type="checkbox"
            checked={showDots}
            onChange={() => setShowDots(!showDots)}
          />{" "}
          Show GPS Points
        </label>
      </div>

      {/* 🔧 Sliders for transform */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 10,
        }}
      >
        {["a", "e", "c", "f", "rotation"].map((k) => (
          <div key={k}>
            <label>
              {k}: {transform[k].toFixed(2)}
              <input
                type="range"
                min={k === "rotation" ? -180 : -500000}
                max={k === "rotation" ? 180 : 500000}
                step={k === "rotation" ? 0.5 : 500}
                value={transform[k]}
                onChange={(e) => handleTransformChange(k, e.target.value)}
                style={{ width: "100%" }}
              />
            </label>
          </div>
        ))}
      </div>

      {/* 🏁 SVG Layer */}
      <div style={{ marginTop: 20, position: "relative" }}>
        <svg
          width="100%"
          height="800"
          viewBox="0 0 2000 800"
          style={{
            background: "linear-gradient(135deg, #0a0015 0%, #1a0033 100%)",
            border: "2px solid #7b2cbf",
            display: "block",
            boxShadow: '0 0 40px rgba(157, 78, 221, 0.4)'
          }}
        >
          {/* 🔲 Alignment Grid */}
          <defs>
            <pattern
              id="smallGrid"
              width="50"
              height="50"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 50 0 L 0 0 0 50"
                fill="none"
                stroke="#222"
                strokeWidth="0.5"
              />
            </pattern>
            <pattern
              id="grid"
              width="500"
              height="500"
              patternUnits="userSpaceOnUse"
            >
              <rect width="500" height="500" fill="url(#smallGrid)" />
              <path
                d="M 500 0 L 0 0 0 500"
                fill="none"
                stroke="#444"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* 👻 Spooky Track Background */}
          {showTrack && (
            <image
              href="/trackspooky.png"
              x="0"
              y="0"
              width="100%"
              height="800"
              opacity="0.5"
              preserveAspectRatio="xMidYMid meet"
              style={{ filter: 'hue-rotate(270deg) saturate(1.5)' }}
            />
          )}

          {/* 👻 Ghostly Racing Line */}
          {showLine && (
            <polyline
              points={linePoints}
              fill="none"
              stroke="#c77dff"
              strokeWidth="4"
              strokeLinecap="round"
              style={{ 
                filter: "drop-shadow(0 0 15px #9d4edd) drop-shadow(0 0 30px #7b2cbf)",
                opacity: 0.9
              }}
            />
          )}

          {/* 🎃 Spooky GPS Points */}
          {showDots && points.map(({ lat, lon }, i) => {
            const { pixelX, pixelY } = gpsToPixel(lat, lon, minLat, minLon, center);
            return (
              <circle
                key={i}
                cx={pixelX}
                cy={pixelY}
                r="3"
                fill="#ff6b35"
                stroke="#ffd60a"
                strokeWidth="1"
                style={{ filter: 'drop-shadow(0 0 4px #ff6b35)' }}
              />
            );
          })}
                    {/* 👻 Phantom Car */}
          <g>
            <circle
              cx={carPixel.pixelX}
              cy={carPixel.pixelY}
              r="12"
              fill="#9d4edd"
              stroke="#c77dff"
              strokeWidth="3"
              opacity="0.6"
              style={{ filter: "drop-shadow(0 0 20px #9d4edd) drop-shadow(0 0 40px #7b2cbf)" }}
            />
            <circle
              cx={carPixel.pixelX}
              cy={carPixel.pixelY}
              r="6"
              fill="#fff"
              opacity="0.8"
            />
            <text
              x={carPixel.pixelX}
              y={carPixel.pixelY + 5}
              fontSize="20"
              textAnchor="middle"
            >
              👻
            </text>
          </g>
        </svg>
      </div>
    </div>
  );
}